import { useState } from "react";
import type { CustomerListRow } from "@/api/customers";
import { formatCurrency } from "@/lib/format";
import { ShieldAlert, Wallet } from "lucide-react";
import { CreditHistoryDialog } from "./credit-history-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface CustomerBalanceCellProps {
  customerId: string;
  customerName: string;
  balance: CustomerListRow["creditBalance"] | null | undefined;
}

// The ledger balance is keyed by OUR customer id — no Stripe account needed
// (refunds/adjustments/migration can credit customers who never saved a card).
export function CustomerBalanceCell({
  customerId,
  customerName,
  balance,
}: CustomerBalanceCellProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  if (
    !balance ||
    !Number.isSafeInteger(balance.balanceCents) ||
    typeof balance.frozen !== "boolean"
  ) {
    return (
      <span
        className="text-muted-foreground"
        title="Credit balance unavailable"
      >
        Unavailable
      </span>
    );
  }
  const { balanceCents, frozen } = balance;

  return (
    <>
      <Button
        variant="ghost"
        className="flex items-center gap-2 h-auto p-0 hover:bg-transparent"
        onClick={() => setDialogOpen(true)}
      >
        <Wallet className="h-4 w-4 text-gray-400" />
        <span
          className={`underline decoration-dotted underline-offset-4 ${
            balanceCents < 0
              ? "font-medium text-red-600"
              : balanceCents > 0
                ? "font-medium text-green-600"
                : "text-gray-500"
          }`}
        >
          {formatCurrency(balanceCents)}
        </span>
        {frozen && (
          <Badge variant="destructive" className="gap-1">
            <ShieldAlert className="h-3 w-3" />
            Frozen
          </Badge>
        )}
      </Button>

      {/* Mounted lazily — every table row renders this cell, and a mounted
          dialog fires its ledger query even while closed. */}
      {dialogOpen && (
        <CreditHistoryDialog
          customerId={customerId}
          customerName={customerName}
          frozen={frozen}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
        />
      )}
    </>
  );
}
